import React from 'react';
import { Outlet } from 'react-router-dom';
import SiteAccessGate from '../SiteAccessGate';

/**
 * Layout route that puts the site-access password gate in front of its
 * children (W-0).
 *
 * WHY A LAYOUT ROUTE. The gate used to wrap the entire <Router>, which put it
 * outside routing altogether — it could not tell one path from another, so
 * taking it off the marketing pages was impossible without taking it off the
 * whole product.
 *
 * The tempting fix is a `window.location.pathname` check before the Router.
 * That is wrong: it is evaluated once at mount and never re-runs on
 * client-side navigation, so a visitor could land on a public page and then
 * SPA-navigate straight into the gated app with no prompt. Routing has to do
 * the work, which is what this does — same pattern as MemberProtectedLayout.
 *
 * Public routes live outside this element in App.tsx; everything else is
 * nested inside it.
 */

export const isGatedHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host.endsWith('.local') ||
    // Staging pilot runs gate-OFF: the org's domain-restricted-sharing policy
    // blocks a publicly-invokable verifySiteAccess gate function. Access is
    // controlled by Firebase login + SEC-2 Firestore rules instead. Production
    // (meliohealthmgmt.co.uk) stays gated.
    host === 'melio-health-staging.web.app' ||
    host === 'melio-health-staging.firebaseapp.com'
  ) {
    return false;
  }
  return true;
};

const GatedLayout: React.FC = () => {
  if (!isGatedHost()) return <Outlet />;
  return (
    <SiteAccessGate>
      <Outlet />
    </SiteAccessGate>
  );
};

export default GatedLayout;
